import { InjectionToken } from '@angular/core';
import { environment } from '@app/env';

export let APP_CONFIG = new InjectionToken<IAppConfig>('app.config');

export const longDate: { [lang: string]: { [key: string]: string } } = {
  en_GB: {
    today: 'Today',
    tomorrow: 'Tomorrow',
    todaysMenu: 'Your menu for today',
    tomorrowsMenu: 'Your menu for tomorrow',
    yesterdayMenu: 'Yesterday',
    twoDaysAgo: 'Two days ago',
    last: 'last',
    lastWeek: 'last week',
    week: 'week',
    weeks: 'weeks',
    ago: 'ago',
    this: 'This',
    next: 'Next',
    zero: 'zero',
    st: 'st',
    nd: 'nd',
    rd: 'rd',
    th: 'th'
  },
  en_IN: {
    today: 'Today',
    tomorrow: 'Tomorrow',
    todaysMenu: 'Your menu for today',
    tomorrowsMenu: 'Your menu for tomorrow',
    yesterdayMenu: 'Yesterday',
    twoDaysAgo: 'Two days ago',
    last: 'last',
    lastWeek: 'last week',
    week: 'week',
    weeks: 'weeks',
    ago: 'ago',
    this: 'This',
    next: 'Next',
    zero: 'zero',
    st: 'st',
    nd: 'nd',
    rd: 'rd',
    th: 'th'
  },
  hi_IN: {
    today: 'आज',
    tomorrow: 'आने वाला कल',
    todaysMenu: 'आज के लिए आपका मेनू',
    tomorrowsMenu: 'कल आ रहा है',
    yesterdayMenu: 'बिता कल',
    twoDaysAgo: 'दो दिन पहले',
    last: 'अंतिम',
    lastWeek: 'पिछले हफ्ते',
    week: 'सप्ताह',
    weeks: 'हफ्ते',
    ago: 'पहले',
    this: 'यह',
    next: 'अगला',
    zero: '0',
    st: '',
    nd: '',
    rd: '',
    th: ''
  },
  et_EE: {
    today: 'Täna',
    tomorrow: 'Homme',
    todaysMenu: 'Sinu tänane menüü',
    tomorrowsMenu: 'Homne menüü',
    yesterdayMenu: 'Eilne menüü',
    twoDaysAgo: 'Kaks päeva tagasi',
    last: 'Viimane',
    lastWeek: 'eelmine nädal',
    week: 'nädal',
    weeks: 'nädalat',
    ago: 'tagasi',
    this: 'See',
    next: 'Järgmine',
    zero: '0',
    st: '',
    nd: '',
    rd: '',
    th: ''
  },
  ru_EE: {
    today: 'Сегодня',
    tomorrow: 'Завтра',
    todaysMenu: 'Ваше меню на сегодня',
    tomorrowsMenu: 'Предстоит завтра',
    yesterdayMenu: 'Вчера',
    twoDaysAgo: 'Два дня назад',
    last: 'Прошл.',
    lastWeek: 'на прошлой неделе',
    week: 'неделю',
    weeks: 'неделя',
    ago: 'назад',
    this: 'Текущ.',
    next: 'Следующ.',
    zero: 'ноль',
    st: '-й',
    nd: '-й',
    rd: '-й',
    th: '-й'
  }
};

export const weekDays: { [lang: string]: string[] } = {
  en_GB: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  en_IN: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  hi_IN: ['रविवार', 'सोमवार', 'मंगलवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'],
  et_EE: ['pühapäev', 'esmaspäev', 'teisipäev', 'kolmapäev', 'neljapäev', 'reede', 'laupäev'],
  ru_EE: ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота']
};

const ones: { [lang: string]: string[] } = {
  en_GB: ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'],
  en_IN: ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'],
  hi_IN: ['', 'एक', 'दो', 'तीन', 'चार', 'पंज', 'छह', 'सात', 'आठ', 'नौ'],
  et_EE: ['', 'üks', 'kaks', 'kolm', 'neli', 'viis', 'kuus', 'seitse', 'kaheksa', 'üheksa'],
  ru_EE: ['', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять']
};

const tens: { [lang: string]: string[] } = {
  en_GB: ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'],
  en_IN: ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'],
  hi_IN: ['', '', 'बीस', 'तीस', 'चालीस', 'पचास', 'साठ', 'सत्तर', 'अस्सी', 'नव्वे'],
  et_EE: [
    '',
    '',
    'kakskümmend',
    'kolmkümmend',
    'nelikümmend',
    'viiskümmend',
    'kuuskümmend',
    'seitsekümmend',
    'kaheksakümmend',
    'üheksakümmend'
  ],
  ru_EE: ['', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят', 'восемьдесят', 'девяносто']
};

const teens: { [lang: string]: string[] } = {
  en_GB: ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'],
  en_IN: ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'],
  hi_IN: ['दस', 'ग्यारह', 'बारह', 'तेरह', 'चौदह', 'पंद्रह', 'सोलह', 'सत्रह', 'अठारह', 'उन्नीस'],
  et_EE: [
    'kümme',
    'üksteist',
    'kaksteist',
    'kolmteist',
    'neliteist',
    'viisteist',
    'kuusteist',
    'seitseteist',
    'kaheksateist',
    'üheksateist'
  ],
  ru_EE: [
    'десять',
    'одиннадцать',
    'двенадцать',
    'тринадцать',
    'четырнадцать',
    'пятнадцать',
    'шестнадцать',
    'семнадцать',
    'восемнадцать',
    'девятнадцать'
  ]
};

export interface IAppConfig {
  profileMinAge: number;
  profileMaxAge: number;
  profileMinHeight: number;
  profileMaxHeight: number;
  profileMinWeight: number;
  profileMaxWeight: number;
  familyMinAge: number;
  familyMaxAge: number;
  familyMinHeight: number;
  familyMaxHeight: number;
  familyMinWeight: number;
  familyMaxWeight: number;
  idealWeightRangeMin: number;
  idealWeightRangeMax: number;
  maternityRangeMin: number;
  maternityRangeMax: number;
  subscriptionEndingInDays: number;
  remindWeightUpdateInDays: number;
  refundInDays: number;
  ingredientIsPerishableInDays: number;
  weekDays: string[];
  longDate: { [key: string]: string };
  ones: string[];
  tens: string[];
  teens: string[];
}

export const DIETLESS_CONFIG: IAppConfig = {
  profileMinAge: 18,
  profileMaxAge: 99,
  profileMinHeight: 100,
  profileMaxHeight: 250,
  profileMinWeight: 23,
  profileMaxWeight: 300,
  familyMinAge: 5,
  familyMaxAge: 99,
  familyMinHeight: 70,
  familyMaxHeight: 250,
  familyMinWeight: 10,
  familyMaxWeight: 300,
  idealWeightRangeMin: 0.9,
  idealWeightRangeMax: 2,
  maternityRangeMin: 18,
  maternityRangeMax: 55,
  subscriptionEndingInDays: 3,
  remindWeightUpdateInDays: 7,
  refundInDays: 14,
  ingredientIsPerishableInDays: 2,
  weekDays: weekDays[environment.lang],
  longDate: longDate[environment.lang],
  ones: ones[environment.lang],
  tens: tens[environment.lang],
  teens: teens[environment.lang]
};
